import { Component, OnDestroy, inject, OnInit, signal } from '@angular/core';
import { AddressesService } from "src/app/services/addresses.service"
import { FormControl, FormGroup, Validators } from "@angular/forms"
import { BehaviorSubject, combineLatest } from "rxjs"
import { AvailabilityService } from "src/app/services/availability.service"
import { ProductsService } from "src/app/services/products.service"
import { Product } from "src/app/meta/Product.interface"
import { ActivatedRoute, Router } from "@angular/router"
import { StepsService } from 'src/app/services/steps.service';

@Component({
  selector: "ow-availability-check",
  templateUrl: "./availability-check.component.html"
})
export class AvailabilityCheckComponent implements OnInit, OnDestroy {
  public readonly addressesService = inject(AddressesService)
  public readonly availabilityService = inject(AvailabilityService)
  public readonly productsService = inject(ProductsService)
  public readonly router = inject(Router)
  private readonly route = inject(ActivatedRoute)
  private readonly stepsService = inject(StepsService)

  public readonly form = new FormGroup({
    postalCode: new FormControl<string>(this.addressesService.getPostalCode(), Validators.required),
    city: new FormControl<string>(this.addressesService.getCity(), Validators.required),
    streetName: new FormControl<string>(this.addressesService.getStreetName(), Validators.required),
    streetNumber: new FormControl<string>(this.addressesService.getStreetNumber(), Validators.required)
  })

  public readonly postalCodeOptions$ = new BehaviorSubject<{ value: string; label: string }[]>([])
  public readonly cityOptions$ = new BehaviorSubject<{ value: string; label: string }[]>([])
  public readonly streetNameOptions$ = new BehaviorSubject<{ value: string; label: string }[]>([])
  public readonly streetNumberOptions$ = new BehaviorSubject<{ value: string; label: string }[]>([])

  public isBusy = signal<boolean>(false)

  private readonly subscriptions = [
    this.availabilityService.availability$.subscribe((availability) => {
      if (
        availability
        && availability.isAvailable
        && this.route.snapshot.queryParams["addressId"]
        && Number(this.route.snapshot.queryParams["addressId"]) === availability.addressId
        && this.route.snapshot.queryParams["productId"]
      ) {
        const initialProduct = availability.availableProducts.find((product) => (product.productId === Number(this.route.snapshot.queryParams["productId"])))
        if (initialProduct) {
          this.chooseBaseProduct(initialProduct)
        }
      }
    }),
    this.addressesService.initialAddressFetched$.subscribe((initialAddressFetched) => {
      if (initialAddressFetched) {
        this.form.patchValue({
          postalCode: this.addressesService.getPostalCode(),
          city: this.addressesService.getCity(),
          streetName: this.addressesService.getStreetName(),
          streetNumber: this.addressesService.getStreetNumber(),
        })
        this.checkAvailability()
      }
    }),
    this.addressesService.postalCodes$.subscribe((postalCodes) => {
      this.postalCodeOptions$.next(
        postalCodes.map((postalCode) => ({
          value: postalCode,
          label: postalCode
        }))
      )
    }),
    this.addressesService.cities$.subscribe((cities) => {
      this.cityOptions$.next(cities.map((city) => ({ value: city, label: city })))
    }),
    this.addressesService.streetNames$.subscribe((streetNames) => {
      this.streetNameOptions$.next(
        streetNames.map((streetName) => ({
          value: streetName,
          label: streetName
        }))
      )
    }),
    this.addressesService.streetNumbers$.subscribe((streetNumbers) => {
      this.streetNumberOptions$.next(
        streetNumbers.map((streetNumber) => ({
          value: streetNumber,
          label: streetNumber
        }))
      )
    }),
    this.form.controls.postalCode.valueChanges.subscribe((postalCode) => {
      this.addressesService.setPostalCode(postalCode!)
    }),
    this.form.controls.city.valueChanges.subscribe((city) => {
      this.addressesService.setCity(city!)
    }),
    this.form.controls.streetName.valueChanges.subscribe((streetName) => {
      this.addressesService.setStreetName(streetName!)
    }),
    this.form.controls.streetNumber.valueChanges.subscribe((streetNumber) => {
      this.addressesService.setStreetNumber(streetNumber!)
    }),
    combineLatest([this.addressesService.isBusy$, this.availabilityService.isBusy$]).subscribe(([addressesServiceIsBusy, availabilityServiceIsBusy]) => {
      this.isBusy.set((addressesServiceIsBusy || availabilityServiceIsBusy))
    })
  ]

  ngOnInit(): void {
    this.stepsService.enableStep("availability-check")
    this.route.queryParams.subscribe((queryParams) => {
      if (queryParams["addressId"]) {
        this.addressesService.fetchInitialAddress(Number(queryParams["addressId"]))
      }
    })
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe()
    })
  }

  public checkAvailability(): void {
    this.availabilityService.check(
      this.addressesService.getPostalCode(),
      this.addressesService.getCity(),
      this.addressesService.getStreetName(),
      this.addressesService.getStreetNumber()
    )
  }

  public chooseBaseProduct(product: Product): void {
    this.productsService.setBaseProduct(product)
    this.stepsService.enableStep("product-options")
    this.router.navigateByUrl("/product-options")
  }
}
